import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Row,
} from "reactstrap";
import { ExecutionStatus, UserPermissions } from "../types";
import { useAskQuestion } from "../hooks/useAskQuestion";
import { useExecutionInfo } from "../hooks/useExecutionInfo";
import { useQueryClient } from "react-query";
import { useStoreActions } from "../stores/store";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useGetQuestions } from "../hooks/useGetQuestions";
import SingleQuestion from "./SingleQuestion";
import Markdown from "markdown-to-jsx";

const QnASection: React.FC<{
  id: string;
}> = ({ id }) => {
  const updateSearchTerm = useStoreActions((s) => s.updateSearchTerm);
  const { data: user } = useCurrentUser();

  const { data: executionData } = useExecutionInfo(id);

  const [collapsed, setCollapsed] = useState(true);
  const [question, setQuestion] = useState("");
  const [finalQuestion, setFinalQuestion] = useState("");
  const queryClient = useQueryClient();
  const [activeSource, setActiveSource] = useState(0);

  const {
    data: qnaResponse,
    refetch: askQuestion,
    isFetching: isLoading,
    isError,
  } = useAskQuestion(id || -1, finalQuestion);

  // console.log("id -- ", id, typeof id);

  const { data: questions, refetch: getQuestions } = useGetQuestions(id || 0, {
    enabled: typeof id === "string",
  });

  useEffect(() => {
    if (id && typeof id === "string") {
      getQuestions();
    }
  }, [id, getQuestions]);

  const pastQuestions = useMemo(() => {
    if (!questions) return [];
    return questions;
  }, [questions]);

  // console.log("pastQuestions -- ", pastQuestions);

  const handleAskQuestion = () => {
    if (!executionData || isLoading) {
      return;
    }
    if (executionData?.execution.status !== ExecutionStatus.completed) {
      alert("The execution is not completed!");
      return;
    }
    if (!executionData.subtitlesTextUrl) {
      alert("No transcript to query");
      return;
    }

    if (!finalQuestion || finalQuestion.trim().length === 0) {
      alert("Please enter a question");
      return;
    }

    queryClient.removeQueries(["qna"]);
    askQuestion();
    getQuestions();
    if (collapsed) {
      setCollapsed(false);
    }
  };

  const handleSearch = (t: string) => {
    updateSearchTerm(t);
    const elem = document.querySelector("#subt_search");
    if (elem) elem.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (finalQuestion) handleAskQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalQuestion]);

  return (
    <>
      <Row>
        <Col className="order-xl-1">
          <Card className="shadow">
            <CardHeader className="bg-white border-0">
              <Row className="align-items-center">
                <Col xs="8" sm="3">
                  <h3 className="mb-0">
                    {pastQuestions.length > 0
                      ? `(${pastQuestions.length}) `
                      : ""}
                    Q&A
                  </h3>
                  {qnaResponse?.cost &&
                    user?.roles.includes(UserPermissions.admin) && (
                      <small>
                        (Answer cost: $${qnaResponse.cost.toFixed(4)})
                      </small>
                    )}
                </Col>
                <Col xs="8" sm="5">
                  <div className="input-group-alternative input-group">
                    <div
                      className="input-group-prepend"
                      style={{ width: "100%" }}
                    >
                      <span
                        className="input-group-text"
                        onClick={() => setFinalQuestion(question)}
                      >
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ask a question"
                        value={question}
                        onChange={(e) => {
                          setQuestion(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key.toLowerCase() === "enter") {
                            setFinalQuestion(question);
                          }
                        }}
                      />
                    </div>
                  </div>
                </Col>
                <Col>
                  <span className="float-right">
                    <Button
                      size="sm"
                      color="primary"
                      onClick={() => {
                        setCollapsed(!collapsed);
                      }}
                    >
                      {collapsed ? "Expand" : "Collapse"}
                    </Button>
                  </span>
                </Col>
              </Row>
            </CardHeader>
            <Collapse isOpen={!collapsed}>
              <CardBody className="overflow-auto">
                {isLoading ? (
                  <div className="text-center">Loading...</div>
                ) : isError ? (
                  <div className="text-center">Error</div>
                ) : !qnaResponse ? null : qnaResponse.responses.length === 0 ? (
                  <div className="text-center">No results found</div>
                ) : (
                  <Row className="ques-resp-col">
                    <Col
                      xs="6"
                      className="ques-resp-col"
                      style={{
                        overflowY: "scroll",
                        maxHeight: "400px",
                      }}
                    >
                      <h2
                        className="row-h2 text-center"
                        style={{
                          position: "sticky",
                          top: "0",
                          backgroundColor: "white",
                        }}
                      >
                        Output
                      </h2>
                      <ul>
                        {qnaResponse.responses.map((item, index) => (
                          <li
                            className="li-resp-col"
                            style={{ cursor: "pointer" }}
                            key={index}
                            onClick={() => {
                              setActiveSource(index);
                            }}
                          >
                            <Markdown options={{ forceBlock: true }}>{item || ""}</Markdown>
                          </li>
                        ))}
                      </ul>
                      {qnaResponse.sources.length === 0 && (
                        <h3 style={{ color: "red" }}>
                          {qnaResponse.responses}
                        </h3>
                      )}
                    </Col>
                    <Col
                      xs="6"
                      className="ques-resp-col"
                      style={{
                        overflowY: "scroll",
                        maxHeight: "400px",
                      }}
                    >
                      <h2
                        className="row-h2 text-center"
                        style={{
                          position: "sticky",
                          top: "0",
                          backgroundColor: "white",
                        }}
                      >
                        Sources
                      </h2>
                      <ul>
                        {qnaResponse.sources[activeSource] &&
                          qnaResponse.sources[activeSource].map((item, idx) => (
                            <li
                              className="li-source-col"
                              style={{ cursor: "pointer" }}
                              key={idx}
                              onClick={() => {
                                if (item.value) handleSearch(item.value);
                              }}
                            >
                              {item.value}
                            </li>
                          ))}
                      </ul>
                    </Col>
                  </Row>
                )}

                {pastQuestions.length > 0 && (
                  <>
                    <hr />
                    {pastQuestions.map((q, i) => (
                      <SingleQuestion
                        key={i}
                        question={q}
                      />
                    ))}
                  </>
                )}
              </CardBody>
            </Collapse>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default QnASection;
